// useMobileMode — global hook for "Mobile Mode" toggle across POS + Quick Order.
//
// Behaviour:
//   * Reads `lagMobileMode` from localStorage (`"on" | "off" | null`).
//   * If null, falls back to viewport width: forces ON when window.innerWidth < 768.
//   * Exposes { mobileMode, setMobileMode, isPhone } so callers can react.
//   * Adds/removes a `mobile-mode` class on <body> so global CSS overrides apply
//     across every nested component without each one having to read the hook.
//
// Used by AdminPOSPage, AdminQuickOrderPage, AdminPage (landing) so the toggle
// state stays in sync across screens.

import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "lagMobileMode";
const PHONE_BREAKPOINT = 768;

function readStored() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function computeInitial() {
  const stored = readStored();
  if (stored === "on") return true;
  if (stored === "off") return false;
  // Auto-detect: force ON for phone-sized viewports
  if (typeof window !== "undefined" && window.innerWidth < PHONE_BREAKPOINT) {
    return true;
  }
  return false;
}

let listeners = new Set();
let globalState = null;

function broadcast(next) {
  globalState = next;
  listeners.forEach((fn) => fn(next));
}

export function useMobileMode() {
  const [mobileMode, setMobileModeState] = useState(() => {
    if (globalState !== null) return globalState;
    globalState = computeInitial();
    return globalState;
  });

  // Subscribe to global changes so toggling on one page updates the others.
  useEffect(() => {
    const handler = (next) => setMobileModeState(next);
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }, []);

  // Sync to <body> class so global CSS can target it.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("mobile-mode", !!mobileMode);
  }, [mobileMode]);

  // Auto-resize listener — only matters when the user hasn't set a manual pref.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      const stored = readStored();
      if (stored === "on" || stored === "off") return; // honour explicit pref
      const next = window.innerWidth < PHONE_BREAKPOINT;
      if (next !== globalState) broadcast(next);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const setMobileMode = useCallback((next) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? "on" : "off");
    } catch {
      /* ignore */
    }
    broadcast(!!next);
  }, []);

  const isPhone =
    typeof window !== "undefined" && window.innerWidth < PHONE_BREAKPOINT;

  return { mobileMode, setMobileMode, isPhone };
}

export default useMobileMode;
